/* eslint-disable @typescript-eslint/no-useless-constructor */
import { SupabaseClient } from '@supabase/supabase-js';
import { ClassProfile } from '@grcs/types/DataTypes';
import { BaseRepository } from '../BaseRepository';
import { DatabaseArrayResult, TABLE_NAMES } from '../types';

/**
 * Class repository that handles all class-related database operations
 * Preserves all existing function signatures and business logic
 */
export class ClassRepository extends BaseRepository {
  constructor(supabase: SupabaseClient<any, 'public', any>) {
    super(supabase);
  }

  /**
   * Preserves exact logic from getClassesBySemester
   */
  async getClassesBySemester(
    semester: string,
  ): Promise<DatabaseArrayResult<ClassProfile>> {
    if (!semester) throw new Error('Semester is not defined');
    return this.getDataById(TABLE_NAMES.CLASS, 'semester_year', semester);
  }

  /**
   * Preserves exact logic from getClassesByTeacherId
   */
  async getClassesByTeacherId(
    teacherId: string,
  ): Promise<DatabaseArrayResult<ClassProfile>> {
    if (!teacherId) throw new Error('Teacher id is not defined');
    return this.getDataById(TABLE_NAMES.CLASS, 'teacher_token', teacherId);
  }

  /**
   * Preserves exact logic from getClassByNameAndSemester
   */
  async getClassByNameAndSemester(
    className: string,
    semester: string,
    excludeDeletedColumn = false,
  ): Promise<DatabaseArrayResult<ClassProfile>> {
    try {
      if (excludeDeletedColumn) {
        const { data, error } = await this.supabase
          .from(TABLE_NAMES.CLASS)
          .select('*')
          .eq('class_name', className)
          .eq('semester_year', semester);
        return { data, error };
      } else {
        const { data, error } = await this.supabase
          .from(TABLE_NAMES.CLASS)
          .select('*')
          .eq('class_name', className)
          .eq('semester_year', semester)
          .eq('deleted', false);
        return { data, error };
      }
    } catch (error) {
      console.error(`Error getting data from ${TABLE_NAMES.CLASS}:`, error);
      return { data: null, error };
    }
  }

  /**
   * Preserves exact logic from getClassInfoByClassId
   */
  async getClassInfoByClassId(
    classId: string,
  ): Promise<DatabaseArrayResult<ClassProfile>> {
    if (!classId) throw new Error('Class id is not defined');
    return this.getDataById(TABLE_NAMES.CLASS, 'class_token', classId);
  }

  /**
   * Preserves exact logic from addNewClassByNameAndSemester
   */
  async addNewClassByNameAndSemester(
    className: string,
    semester: string,
    teacherToken?: string,
  ): Promise<DatabaseArrayResult<ClassProfile>> {
    return this.insertData(TABLE_NAMES.CLASS, {
      class_name: className,
      semester_year: semester,
      teacher_token: teacherToken,
    });
  }

  /**
   * Preserves exact logic from getAllClasses
   */
  async getAllClasses(): Promise<DatabaseArrayResult<ClassProfile>> {
    try {
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.CLASS)
        .select('*');

      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Generic method to insert data into class table
   */
  async insertData(
    tableName: string,
    data: any,
  ): Promise<DatabaseArrayResult<any>> {
    return super.insertData(tableName, data);
  }

  /**
   * Generic method to update data by ID
   */
  async updateDataById(
    tableName: string,
    idName: string,
    idValue: string,
    updateData: any,
  ): Promise<DatabaseArrayResult<any>> {
    return super.updateDataById(tableName, idName, idValue, updateData);
  }

  /**
   * Generic method to soft delete by ID
   */
  async softDeleteById(
    tableName: string,
    idName: string,
    idValue: string,
  ): Promise<void> {
    return super.softDeleteByIdPublic(tableName, idName, idValue);
  }

  /**
   * Generic method to get all data
   */
  async getAllData<T>(
    tableName: string,
    excludeDeleted = true,
  ): Promise<DatabaseArrayResult<T>> {
    return super.getAllDataPublic<T>(tableName, excludeDeleted);
  }

  /**
   * Update class student count - called when students are added/removed
   */
  async updateClassStudentCount(
    classId: string,
    semesterYear: string,
  ): Promise<DatabaseArrayResult<any>> {
    if (!classId || !semesterYear) {
      throw new Error('Class ID and semester year are required');
    }

    try {
      // Count current students in this class for this semester
      const { count, error: countError } = await this.supabase
        .from(TABLE_NAMES.STUDENT)
        .select('*', { count: 'exact', head: true })
        .eq('class_token', classId)
        .eq('semester_year', semesterYear)
        .eq('deleted', false);

      if (countError || typeof count !== 'number')
        return {
          data: null,
          error: countError || new Error('Class count unavailable'),
        };

      // Update the class with the current student count
      const { data, error } = await this.supabase
        .from(TABLE_NAMES.CLASS)
        .update({ number_of_students: count || 0 })
        .eq('class_token', classId)
        .eq('semester_year', semesterYear)
        .select();

      if (error) {
        console.error('Error updating class student count:', error);
        return { data: null, error };
      }

      console.log(`Updated class ${classId} student count to ${count}`);
      return { data, error: null };
    } catch (error) {
      console.error('Error updating class student count:', error);
      return { data: null, error };
    }
  }
}
